/**
 * Formatteurs purs de l'écran Administration (Sprint 14, PRD Module 15).
 *
 * Aucune valeur de couleur ici : les fonctions qui qualifient un état
 * renvoient un « ton » de badge (`BadgeTone`) consommé par `Badge`, exactement
 * comme `suivi-format.ts`. Les libellés sont en français, côté client
 * uniquement — l'API ne renvoie que des identifiants stables.
 */
import type {
  AccountRole,
  BillingCycle,
  BillingForfait,
  CompteFacturationDto,
  CreditAction,
  NiveauScolaire,
} from '@silex/shared';

import type { BadgeTone } from './suivi-format';

/* ------------------------------------------------------------------ rôles */

const ROLE_LABELS: Record<AccountRole, string> = {
  parent: 'Parent',
  tuteur: 'Tuteur',
  admin: 'Admin',
  eleve: 'Élève',
};

export function accountRoleLabel(role: AccountRole): string {
  return ROLE_LABELS[role] ?? role;
}

/* --------------------------------------------------------------- scolaire */

const NIVEAU_LABELS: Record<NiveauScolaire, string> = {
  sec1: 'Secondaire 1',
  sec2: 'Secondaire 2',
  sec3: 'Secondaire 3',
  sec4: 'Secondaire 4',
  sec5: 'Secondaire 5',
};

export function niveauScolaireLabel(niveau: NiveauScolaire): string {
  return NIVEAU_LABELS[niveau] ?? niveau;
}

/* ------------------------------------------------------------ actions IA */

/**
 * Libellés des actions facturables (`CREDIT_ACTIONS` de `@silex/shared`).
 * Une action inconnue est rendue telle quelle plutôt que masquée : si le
 * barème gagne une action, elle apparaît dans le tableau au lieu de
 * disparaître silencieusement de la répartition.
 */
const ACTION_LABELS: Record<CreditAction, string> = {
  aide_devoirs: 'Aide aux devoirs',
  generation_exercice: "Génération d'exercice",
  evaluation_llm: 'Évaluation IA',
  transcription_audio: 'Transcription audio',
  synthese_vocale: 'Synthèse vocale',
  analyse_vision: "Analyse d'image",
  rapport_parent: 'Rapport parent',
  /* La correction de référence, fabriquée une fois par exercice et relue par
     tous les élèves qui le rencontrent. Nommée « corrigé » et non
     « référence » : c'est le mot que le tableau de bord doit rendre lisible à
     quelqu'un qui regarde une facture, pas notre vocabulaire de schéma. */
  reference_solution: 'Corrigé de référence',
  /* Chantier « manuel vivant », Φ-MV-4 — la lecture de la démarche d'un
     exercice du manuel, à sa propre réserve. */
  correction_exercice: 'Correction d’exercice',
  /* La lecture d'une page écrite au stylet par le lecteur d'encre (9 oct. 2026). */
  lecture_encre: 'Lecture du stylet',
};

export function creditActionLabel(action: CreditAction): string {
  return ACTION_LABELS[action] ?? action;
}

/* ---------------------------------------------------------------- nombres */

const NOMBRE = new Intl.NumberFormat('fr-CA');

/** « 4 187 » — séparateur de milliers, jamais de décimale. */
export function formatNombre(n: number): string {
  return NOMBRE.format(n);
}

const USD = new Intl.NumberFormat('fr-CA', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** « 128,44 $ US » — coûts agrégés. */
export function formatUsd(n: number): string {
  return USD.format(n);
}

const USD_PRECIS = new Intl.NumberFormat('fr-CA', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

/**
 * « 0,0307 $ US » — coût MOYEN par appel. 2 décimales arrondiraient la
 * plupart des actions à 0,03 $ et rendraient la comparaison entre actions
 * illisible, d'où les 4 décimales sur cette mesure précise.
 */
export function formatUsdPrecis(n: number): string {
  return USD_PRECIS.format(n);
}

/** Part en pourcentage entier, bornée — `total` à 0 ne divise jamais. */
export function part(valeur: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((valeur / total) * 100)));
}

/* ------------------------------------------------------------------ dates */

const DATE_COURTE = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const DATE_HEURE = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** « 30 août 2026 ». */
export function formatDate(iso: string): string {
  return DATE_COURTE.format(new Date(iso));
}

/** « 30 août, 17:40 » — activité récente, l'heure compte. */
export function formatDateHeure(iso: string): string {
  return DATE_HEURE.format(new Date(iso));
}

/** `YYYY-MM-DD` en UTC — borne de fenêtre envoyée à l'API. */
export function isoJour(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Qualifie une dernière connexion pour lecture « d'un coup d'œil » (PRD) :
 * jamais connecté et inactivité longue remontent en `a-revoir`.
 */
export function connexionBadge(
  lastLoginAt: string | null,
  maintenant: number = Date.now(),
): { tone: BadgeTone; label: string } {
  if (!lastLoginAt) return { tone: 'a-revoir', label: 'Jamais connecté' };
  const jours = Math.floor((maintenant - new Date(lastLoginAt).getTime()) / 86_400_000);
  if (jours <= 0) return { tone: 'acquis', label: "Aujourd'hui" };
  if (jours === 1) return { tone: 'acquis', label: 'Hier' };
  if (jours <= 7) return { tone: 'en-cours', label: `Il y a ${jours} jours` };
  return { tone: 'a-revoir', label: `Il y a ${jours} jours` };
}

/**
 * Qualifie l'activité d'un élève sur la semaine. Le seuil bas (aucune
 * tentative) est le seul signal « à revoir » : il n'existe pas de dose cible
 * cotée côté API, donc aucun ratio n'est inventé ici.
 */
export function activiteEleveBadge(nbTentatives: number): { tone: BadgeTone; label: string } {
  if (nbTentatives === 0) return { tone: 'a-revoir', label: 'Aucune activité' };
  if (nbTentatives < 10) return { tone: 'en-cours', label: 'Activité faible' };
  return { tone: 'acquis', label: 'Actif' };
}

/* ------------------------------------------------------------ facturation */

const FORFAIT_LABELS: Record<BillingForfait, string> = {
  francais: 'Français',
  mathematiques: 'Mathématiques',
  combine: 'Combiné',
};

const CYCLE_LABELS: Record<BillingCycle, string> = {
  mensuel: 'mensuel',
  annuel: 'annuel',
};

const MONTANT_CAD = new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' });

/**
 * L'état de facturation d'un compte, pour l'exploitant (fondateur, 23 oct.
 * 2026) — lu en base, jamais chez le prestataire. Un essai échu reste
 * `essai` jusqu'à ce que le prestataire l'annule, puis devient `resilie` en
 * gardant `essaiFin` : les deux se lisent « Essai terminé », comme dans
 * l'app du parent (`essaiEchu`).
 */
export function facturationResume(
  f: CompteFacturationDto,
  maintenant: number = Date.now(),
): { tone: BadgeTone; label: string; formule: string; paiement: string } {
  const formule = `${FORFAIT_LABELS[f.formule]} · ${CYCLE_LABELS[f.cycle]}`;
  const paiement =
    f.dernierPaiementLe !== null && f.dernierPaiementCents !== null
      ? `Dernier paiement : ${formatDate(f.dernierPaiementLe)} · ${MONTANT_CAD.format(f.dernierPaiementCents / 100)} HT`
      : 'Aucun paiement';
  const essaiEchu =
    f.essaiFin !== null &&
    (f.statut === 'resilie' ||
      (f.statut === 'essai' && new Date(f.essaiFin).getTime() <= maintenant));
  const etat: { tone: BadgeTone; label: string } = essaiEchu
    ? { tone: 'a-revoir', label: 'Essai terminé' }
    : f.statut === 'essai'
      ? {
          tone: 'en-cours',
          label: f.essaiFin ? `Essai jusqu’au ${formatDate(f.essaiFin)}` : 'Essai en cours',
        }
      : f.statut === 'actif'
        ? { tone: 'acquis', label: 'Actif' }
        : f.statut === 'impaye'
          ? { tone: 'a-revoir', label: 'Paiement non abouti' }
          : f.statut === 'suspendu'
            ? { tone: 'a-revoir', label: 'Suspendu' }
            : { tone: 'a-revoir', label: 'Résilié' };
  return { ...etat, formule, paiement };
}
